import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterModel } from 'ag-grid-enterprise';
import saveAs from 'file-saver';
import { from } from 'rxjs';
import { ProposalRequest } from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { DateRangeSelectionModalComponent } from 'src/app/modals/date-range-selection-modal/date-range-selection-modal.component';
import { VideoPlayerModalComponent } from 'src/app/modals/video-player-modal/video-player-modal.component';
import { CompanyService } from 'src/app/services/company.service';
import { ProposalRequestService } from 'src/app/services/proposal-request.service';
import { ReportService } from 'src/app/services/report.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-quicksource',
  templateUrl: './quicksource.component.html',
  styleUrls: ['./quicksource.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QuicksourceComponent implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private ToastService: ToastService,
    private ProposalRequestService: ProposalRequestService,
    private ModalService: NgbModal,
    private DomSanitizer: DomSanitizer,
    private ReportService: ReportService,
    private CompanyService: CompanyService,
    private UtilityService: UtilityService
  ) {
    const currentNavigation = router.currentNavigation();

    const state = currentNavigation?.extras?.state;

    if (state) {
      const { query, FilterModel, title } = state;

      if (FilterModel) {
        this.FilterModel = FilterModel;
      }

      if (query) {
        Object.assign(this.suppliedGridQueryData, query);
      }

      if (title) {
        this.title = this.title.concat(` - ${title}`);
      }
    }
  }

  CompanyId!: number;
  reloadGrid: boolean = false;
  title: string = 'QuickSourceBids';

  // Display these actions in the action column.
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'cancel', 'stop', 'view', 'search']
  };

  // Some additional data to load on the Client/Vendor grid.
  suppliedGridQueryData: { [key: string]: any } = {
    attributes: ['CompanyId', 'status'],
    status: { $ne: 'CANCELLED' }
  };

  CancelledBidsModel: { show: boolean } = { show: false };

  TooltipMap: {
    [
      key in Extract<
        GridActionColumnActions,
        'edit' | 'cancel' | 'stop' | 'view' | 'search'
      >
    ]: string;
  } = {
    edit: 'Edit the Bid',
    cancel: 'Delete Bid',
    stop: 'Close bid to new submissions',
    view: 'View the Bid',
    search: 'Review/Report/Finalize Bid'
  };

  FilterModel?: FilterModel;

  usesSpotlightVendors: boolean = false;

  createBidTutorialVideo() {
    const { componentInstance } = this.ModalService.open(
      VideoPlayerModalComponent,
      { backdrop: 'static', size: 'xl' }
    );

    Object.assign(componentInstance, {
      IFrameOptions: {
        src: 'https://player.vimeo.com/video/1077666771?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        style: 'width:100%;height:100%;',
        title: 'QSB3'
      }
    });
  }

  manageBidTutorialVideo() {
    const { componentInstance } = this.ModalService.open(
      VideoPlayerModalComponent,
      { backdrop: 'static', size: 'xl' }
    );

    Object.assign(componentInstance, {
      IFrameOptions: {
        src: 'https://player.vimeo.com/video/1080216664?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        style: 'width:100%;height:100%;',
        title: 'QSB management_E1'
      }
    });
  }

  deleteBidChange(show: boolean) {
    Object.assign(this.CancelledBidsModel, { show });

    const suppliedGridQueryData = {
      attributes: ['CompanyId', 'status']
    };

    if (!show) {
      Object.assign(suppliedGridQueryData, { status: { $ne: 'CANCELLED' } });
    }

    this.suppliedGridQueryData = suppliedGridQueryData;
  }

  getDisplayedColumnActions: ({
    rowData
  }: {
    rowData: ProposalRequest;
  }) => GridActionColumnActions[] = ({ rowData }) => {
    const { status } = rowData;

    if (status === 'CREATED') {
      return ['edit', 'cancel'];
    } else if (status === 'RELEASED') {
      return ['edit', 'cancel', 'stop', 'search'];
    } else if (status === 'CANCELLED') {
      return ['view'];
    } else if (status === 'CLOSED') {
      return ['view', 'search'];
    } else {
      return ['view', 'search'];
    }
  };

  addQuickSourceBid() {
    this.router.navigate([
      'client',
      this.CompanyId,
      'proposal-request',
      'site-selection'
    ]);
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    const { id: ProposalRequestId } = rowData;

    if (action === 'edit') {
      this.router.navigate([
        'client',
        this.CompanyId,
        'proposal-request',
        ProposalRequestId
      ]);
    }

    if (action === 'cancel') {
      const ConfirmModal = this.ModalService.open(ConfirmModalComponent, {
        backdrop: 'static',
        modalDialogClass: 'confirm-modal'
      });
      const {
        componentInstance
      }: { componentInstance: ConfirmModalComponent } = ConfirmModal;
      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to cancel this proposal?'
      });
      from(ConfirmModal.result).subscribe({
        next: (response) => {
          if (response) {
            this.ProposalRequestService.saveProposalRequest(
              Object.assign(rowData, { status: 'CANCELLED' })
            ).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully cancelled proposal.'
                );
                this.reloadGrid = true;
              }
            });
          }
        }
      });
    }

    if (action === 'stop') {
      const ConfirmModal = this.ModalService.open(ConfirmModalComponent, {
        backdrop: 'static',
        modalDialogClass: 'confirm-modal'
      });
      const {
        componentInstance
      }: { componentInstance: ConfirmModalComponent } = ConfirmModal;
      Object.assign(componentInstance, {
        displayText:
          'Are you sure you want to close this proposal and end bidding?'
      });
      from(ConfirmModal.result).subscribe({
        next: (response) => {
          if (response) {
            this.ProposalRequestService.saveProposalRequest(
              Object.assign(rowData, { status: 'CLOSED' })
            ).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully closed the proposal.'
                );
                this.reloadGrid = true;
              }
            });
          }
        }
      });
    }

    if (action === 'view') {
      this.router.navigate([
        'client',
        this.CompanyId,
        'proposal-request',
        ProposalRequestId,
        'view',
        'review'
      ]);
    }

    if (action === 'search') {
      this.router.navigate([
        'client',
        this.CompanyId,
        'proposal-request',
        ProposalRequestId,
        'proposals'
      ]);
    }
  }

  // rowClicked(event: any) {
  //   const { data } = event;
  //   const { status } = data;
  //   this.action({ action: status === 'CREATED' ? 'edit' : 'search', rowData: data });
  // };

  generateSpotlightVendorsKPIReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateSpotlightVendorKPIReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(
              new Blob([Report as ArrayBuffer]),
              'SpotlightVendorsKPIReport.xlsx'
            );
          }
        });
      }
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const CompanyId = Number(paramMap.get('CompanyId'));

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
            returnError: true
          });
        }

        this.CompanyId = CompanyId;

        this.CompanyService.getCompany(
          CompanyId,
          this.UtilityService.stringifyObjectsInQueryObject({
            id: CompanyId,
            attributes: ['id'],
            CompanyServiceRanking: {
              attributes: ['id']
            }
          })
        ).subscribe({
          next: (company) => {
            const { CompanyServiceRankings } = company;

            if (CompanyServiceRankings && CompanyServiceRankings.length > 0) {
              this.usesSpotlightVendors = true;
            }
          }
        });
      }
    });
  }
}
